import { SiteResponse, UserResponse } from "../../types";
import { TO_BE_AGREED } from "../../utils";

/**
 * Pure display-formatting helpers extracted out of the Carbon `DataTable` row builders
 * (`materialRows.tsx`) so they're unit-testable without rendering anything. Kept dumb on
 * purpose: no `t()`, no JSX, no knowledge of which screen (`/materials` or `/materials/company`)
 * is asking.
 */

/** `deliveryFormat` renders as "-" when unset, same convention `Sites.tsx`'s `comment` column
 * uses (`site.comment || "-"`) — matched here rather than the pre-Carbon table's `" - "` (a
 * padded string that only looked like "-" once Playwright's whitespace-normalizing text matcher
 * collapsed it). */
export const formatDeliveryFormat = (deliveryFormat?: string | null): string =>
  deliveryFormat ? deliveryFormat : "-";

/** A material with no `deliveryPlace` reads as "To be agreed upon" (`messages.toAgree`, the same
 * `TO_BE_AGREED` constant the pre-Carbon `MaterialTableRow` used) rather than a blank cell. */
export const resolveSiteName = (
  deliveryPlace?: Pick<SiteResponse, "name"> | null
): string => deliveryPlace?.name ?? TO_BE_AGREED;

/**
 * "First Last" built from `firstName`/`lastName` — the exact fields `MaterialResponse`'s
 * `owner`/`user` carry per Task 2's DTO (see task-5-brief.md's DATA SHAPE section) — rather than
 * reading the DTO's own computed `fullName`. This is deliberate: there is an open, separate
 * investigation into `owner`/`user` rendering blank on some detail reads under the current
 * live-chip-churned backend, and this list's own Owner/Responsible column (the company-materials
 * view, in list mode) must not silently inherit that if `fullName` turns out to be the field
 * that's blank while `firstName`/`lastName` are still populated. Falls back to "-" (never an
 * empty cell) only when both names are genuinely missing.
 */
export const getOwnerName = (
  owner?: Pick<UserResponse, "firstName" | "lastName"> | null
): string => {
  if (!owner) return "-";
  const name = [owner.firstName, owner.lastName]
    .filter((part) => Boolean(part && part.trim()))
    .join(" ")
    .trim();
  return name || "-";
};
